(() => {
  const buildContext = (title, values, sizeNote) => [title, values.orientation, values.size || ((values.width_cm || values.height_cm) ? `W ${values.width_cm || '?'} x H ${values.height_cm || '?'} cm (${sizeNote})` : ''), values.package].filter(Boolean).join(' - ');
  class FrameConfig extends HTMLElement {
    text(key, values = {}) {
      this.messages ||= JSON.parse(this.dataset.i18n);
      return this.messages[key].replace(/__([a-z]+)__/g, (token, name) => values[name] ?? token);
    }
    updateSizeLimits(form, editedInput = null, capMaximum = false) {
      const width = form.elements.width_cm;
      const height = form.elements.height_cm;
      if (!width || !height) return;
      const minShort = Number(this.dataset.minShort) || 1;
      const minLong = Number(this.dataset.minLong) || minShort;
      const maxShort = Number(this.dataset.maxShort);
      const maxLong = Number(this.dataset.maxLong);
      const orientation = form.elements.orientation?.value || '';
      const limits = this.querySelector('[data-size-limits]');
      const message = this.querySelector('[data-size-error]');
      width.disabled = height.disabled = !orientation;
      if (!orientation) {
        limits.textContent = this.text('choose_orientation');
        message.hidden = true;
        return;
      }
      if (this.previousOrientation && this.previousOrientation !== orientation) {
        // Rotating preserves the requested format, including a partially entered size.
        [width.value, height.value] = [height.value, width.value];
        delete width.dataset.sizeAdjustment;
        delete height.dataset.sizeAdjustment;
      }
      this.previousOrientation = orientation;
      const landscape = orientation === 'Landscape';
      const wMin = landscape ? minLong : minShort, hMin = landscape ? minShort : minLong;
      const wMax = landscape ? maxLong : maxShort, hMax = landscape ? maxShort : maxLong;
      limits.textContent = (this.dataset.minShort ? this.text('min_size', {width: wMin, height: hMin}) + ' ' : '') + this.text('max_size', {width: wMax, height: hMax});
      if (editedInput === width || editedInput === height) delete editedInput.dataset.sizeAdjustment;
      if (capMaximum) {
        for (const [input, maximum, label] of [[width, wMax, this.text('width_name')], [height, hMax, this.text('height_name')]]) {
          if (input.value !== '' && Number(input.value) > maximum) {
            input.value = String(maximum);
            input.dataset.sizeAdjustment = this.text('limited', {dimension: label, maximum});
          }
        }
      }
      const errors = [];
      const reversed = Number(width.value) > 0 && Number(height.value) > 0 && (landscape ? Number(width.value) < Number(height.value) : Number(width.value) > Number(height.value));
      for (const [input, minimum, maximum, label] of [[width, wMin, wMax, this.text('width_name')], [height, hMin, hMax, this.text('height_name')]]) {
        input.min = String(minimum);
        input.max = String(maximum);
        const value = Number(input.value);
        const error = input.value !== '' && (value < Number(input.min) || value > Number(input.max))
          ? this.text('range_error', {dimension: label, minimum: input.min, maximum: input.max}) : reversed ? this.text(landscape ? 'landscape_error' : 'portrait_error') : '';
        input.setCustomValidity(error);
        input.setAttribute('aria-invalid', error ? 'true' : 'false');
        input.setAttribute('data-size-adjusted', input.dataset.sizeAdjustment ? 'true' : 'false');
        if (error) errors.push(error);
        else if (input.dataset.sizeAdjustment) errors.push(input.dataset.sizeAdjustment);
      }
      message.textContent = [...new Set(errors)].join(' ');
      message.hidden = errors.length === 0;
    }
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const form = this.querySelector('form');
      const contactUrl = this.dataset.contactUrl || `${globalThis.Shopify?.routes?.root || '/'}pages/contact`;
      const templates = JSON.parse(this.querySelector('[data-frame-templates]')?.textContent || '{}');
      const template = this.querySelector('[data-template]');
      const update = (event) => {
        this.updateSizeLimits(form, event?.type === 'input' ? event.target : null, true);
        const values = Object.fromEntries(new FormData(form));
        for (const name of ['orientation', 'package']) {
          const selected = form.querySelector(`[name="${name}"]:checked`);
          if (selected) values[name] = selected.closest('label').textContent.trim();
        }
        const context = buildContext(this.dataset.modelName, values, this.text('size_context'));
        form.elements.product.value = context;
        this.querySelector('[data-replacement-note]').hidden = form.elements.package.value !== 'Replacement print';
        const download = templates[form.elements.size?.value];
        template.href = download || `${contactUrl}#product=${encodeURIComponent(context + ' - ' + this.text('template_context'))}`;
        template.dataset.directDownload = String(Boolean(download));
        if (download) {
          template.target = '_blank';
          template.rel = 'noopener';
        } else {
          template.removeAttribute('target');
          template.removeAttribute('rel');
        }
        template.querySelector('span').textContent = download ? this.dataset.templateDownloadLabel : this.dataset.templateRequestLabel;
        template.dispatchEvent(new Event('templatechange', {bubbles: true}));
        this.closest('[data-frame-page]')?.querySelectorAll('[data-frame-page-quote]').forEach(link => { link.href = `${contactUrl}#product=${encodeURIComponent(context)}`; });
      };
      form.addEventListener('input', update);
      form.addEventListener('change', update);
      form.addEventListener('submit', event => {
        update();
        if (!form.reportValidity()) event.preventDefault();
      });
      const guardedLinks = [this.querySelector('[data-template]'), ...(this.closest('[data-frame-page]')?.querySelectorAll('[data-frame-page-quote]') || [])];
      guardedLinks.forEach(link => link.addEventListener('click', event => {
        update();
        if (!form.reportValidity()) event.preventDefault();
      }));
      update();
    }
  }
  class FrameSelector extends HTMLElement {
    text(key, values = {}) {
      this.messages ||= JSON.parse(this.dataset.i18n);
      return this.messages[key].replace(/__([a-z]+)__/g, (token, name) => values[name] ?? token);
    }
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.cards = [...this.querySelectorAll('[data-frame]')];
      const filters = this.querySelector('[data-frame-filters]');
      const compare = this.querySelector('[data-compare]');
      const dialog = this.querySelector('dialog');
      filters.hidden = false;
      compare.hidden = false;
      this.querySelectorAll('[data-selection]').forEach(el => { el.hidden = false; });
      const selection = () => this.cards.filter(c => c.querySelector('[data-select]').checked);
      const updateSelection = () => {
        const n = selection().length;
        compare.textContent = this.text('compare_selected', {count: n});
        compare.disabled = n < 2;
        this.cards.forEach(c => { const input = c.querySelector('[data-select]'); input.disabled = n >= 5 && !input.checked; });
      };
      const filter = () => {
        const values = Object.fromEntries(new FormData(filters));
        this.cards.forEach(card => {
          card.hidden = Boolean((values.type && card.dataset.type !== values.type) || (values.mount && !card.dataset.mount.split(', ').includes(values.mount)) || (values.format && card.dataset.format !== values.format));
          if (card.hidden) card.querySelector('[data-select]').checked = false;
        });
        const n = this.cards.filter(c => !c.hidden).length;
        this.querySelector('[data-count]').textContent = this.text('model_count', {count: n, total: this.cards.length});
        this.querySelector('[data-empty]').hidden = n > 0;
        this.querySelector('[data-reset]').disabled = !Object.values(values).some(Boolean);
        updateSelection();
      };
      filters.addEventListener('change', filter);
      filters.addEventListener('reset', event => {
        event.preventDefault();
        filters.querySelectorAll('select').forEach(select => { select.selectedIndex = 0; });
        filter();
      });
      this.querySelector('[data-clear]').addEventListener('click', () => filters.reset());
      this.querySelectorAll('[data-select]').forEach(input => input.addEventListener('change', updateSelection));
      compare.addEventListener('click', () => {
        const cards = selection();
        if (cards.length < 2 || cards.length > 5) return;
        const table = dialog.querySelector('table');
        table.replaceChildren();
        const addRow = (label, values, header = false) => {
          const row = document.createElement('tr');
          const heading = document.createElement('th');
          heading.scope = header ? 'col' : 'row';
          heading.textContent = label;
          row.append(heading);
          values.forEach(value => { const cell = document.createElement(header ? 'th' : 'td'); if(header) cell.scope = 'col'; cell.textContent = value; row.append(cell); });
          return row;
        };
        const head = table.createTHead();
        head.append(addRow(this.text('model'), cards.map(c => c.dataset.title), true));
        const body = table.createTBody();
        body.append(addRow(this.text('difference'), cards.map(c=>c.querySelector('.ns-frames__difference').textContent)));
        body.append(addRow(this.text('format'), cards.map(c=>c.dataset.format === 'custom' ? this.text('made_to_size') : [...c.querySelector('[name=size]').options].map(o=>o.text).join('; '))));
        const facts = cards.map(card=>new Map([...card.querySelectorAll('dl > div')].map(div=>[div.querySelector('dt').textContent, div.querySelector('dd').textContent])));
        const labels = [...new Set(facts.flatMap(f=>[...f.keys()]))];
        labels.forEach(label=>body.append(addRow(label,facts.map(f=>f.get(label)||this.text('not_specified')))));
        dialog.showModal();
      });
      this.querySelector('[data-close]').addEventListener('click',()=>dialog.close());
      dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
      filter();
    }
  }
  if (!customElements.get('nordsign-frame-config')) customElements.define('nordsign-frame-config', FrameConfig);
  if (!customElements.get('nordsign-frame-selector')) customElements.define('nordsign-frame-selector', FrameSelector);
})();
